import { getPreferredFullName } from './patient.util';
import {
  type CareTeamProviderType,
  CareTeamProviderTypeNames,
} from '../constants/patients.constants';
import {
  USER_FILTER_ADMIN,
  USER_FILTER_MAUD,
} from '../constants/user.constants';
import { DateTime } from 'luxon';
import type { TableFilter } from '@equip.health/ui';

export const getInitialFilterValue = (): UserListFilterType => {
  return {
    hasAdminPermissions: false,
    hasMaudPermissions: false,
    isSupervisor: false,
    region: null,
    role: null,
    status: null,
    supervisor: null,
    timezone: null,
  };
};

export const getUserListFilterParams = ({
  region,
  timezone,
  role,
  status,
  isSupervisor: _isSupervisor,
  hasAdminPermissions,
  hasMaudPermissions,
}: UserListFilterType): UserListFilterParamType => {
  const filterParams: UserListFilterParamType = {};

  if ((region ?? []).length) {
    filterParams.region = region.map((reg) => reg.value);
  }

  if ((timezone ?? []).length) {
    filterParams.timezone = timezone.map((tz) => tz.value);
  }

  if ((role ?? []).length > 0) {
    filterParams.role = role.map((rl) => rl.value);
  }

  if (status?.value) {
    filterParams.status = status.value;
  }

  /** @TODO We need to have a filter by isSupervisor in the backend */

  // only apply the appAccess filter if at least one access filter is checked
  if (hasAdminPermissions || hasMaudPermissions) {
    const appAccess = [];
    if (hasAdminPermissions) appAccess.push(USER_FILTER_ADMIN);
    if (hasMaudPermissions) appAccess.push(USER_FILTER_MAUD);
    filterParams['app-access'] = appAccess;
  }

  return filterParams;
};

export const getUserListFilterParamsV2 = (
  filters: TableFilter[],
  isProviderTypePkTransitionEnabled?: boolean,
): UserListFilterParamType => {
  const filterParams: UserListFilterParamType = {};
  const getFilter = (key: string) => filters.find((f) => f.filterKey === key);
  const roleFilter = getFilter('role');
  if (roleFilter?.filters?.length) {
    filterParams.role = roleFilter.filters.map((r) =>
      isProviderTypePkTransitionEnabled ? String(r.value) : r.label,
    );
  }

  const statusFilter = getFilter('status');
  if (statusFilter?.filters?.[0]) {
    filterParams.status = String(statusFilter.filters[0].value);
  }

  const permissionsFilter = getFilter('permissions');
  if (permissionsFilter?.filters?.length) {
    const appAccess: string[] = [];

    for (const permission of permissionsFilter.filters) {
      const value = String(permission.value).toLowerCase();

      if (value === USER_FILTER_ADMIN) {
        appAccess.push(USER_FILTER_ADMIN);
      } else if (value === USER_FILTER_MAUD) {
        appAccess.push(USER_FILTER_MAUD);
      }
    }

    if (appAccess.length) {
      filterParams['app-access'] = appAccess;
    }
  }

  return filterParams;
};

export const getPrimaryUserOptionText = ({
  firstName = '',
  lastName = '',
}: {
  firstName?: string;
  lastName?: string;
}): string => {
  return `${firstName ?? ''}${firstName && lastName ? ' ' : ''}${
    lastName ?? ''
  }`;
};
export const getSecondaryUserOptionText = (
  {
    gender,
    dateOfBirth,
    providerTypeName,
  }: Pick<Provider, 'gender' | 'dateOfBirth' | 'providerTypeName'>,
  isProvider?: boolean,
): string[] => {
  if (isProvider) return providerTypeName ? [providerTypeName] : [];
  return [...(gender ? [gender] : []), ...(dateOfBirth ? [dateOfBirth] : [])];
};

// patient should show `chosenName lastName` but also appear in search for `firstName lastName`
// Do not use for GET provider v2 calls - prefer getProviderAutocompleteOption instead
export const getUserOption =
  (isProvider?: boolean, isDisabledCheck?: boolean) =>
  (
    user: Patient | (GetProviderByIdResponse & { calendarId?: string }),
  ): AutoCompleteOption => ({
    isDisabled:
      isProvider &&
      isDisabledCheck &&
      !(user as ProviderCalendarAccount).calendarId,
    primaryText: isProvider
      ? getPrimaryUserOptionText(user)
      : getPreferredFullName(
          (user as Patient).chosenName,
          user.firstName,
          user.lastName,
        ),
    searchOnlyText: isProvider ? undefined : getPrimaryUserOptionText(user),
    secondaryText: getSecondaryUserOptionText(user, isProvider),
    value: user.userId,
  });

export const getProviderAutocompleteOption = (
  provider: GetProviderByIdResponse,
  disableIfMissingCalendarId?: boolean,
): AutoCompleteOption => ({
  isDisabled: disableIfMissingCalendarId && !provider.calendarId,
  primaryText: getPrimaryUserOptionText(provider),
  secondaryText: [provider.providerTypeDetails.name],
  value: provider.userId,
});

export const groupClassHostToAutoCompleteOption = (
  host: GroupClassHost,
): ProviderAutoCompleteOption => {
  return {
    avatar: host?.avatar || undefined,
    email: host.email || '',
    firstName: host.firstName,
    lastName: host.lastName,
    primaryText: `${host.firstName} ${host.lastName}`,
    providerTypeDetails: {
      name: host.providerType,
    },
    secondaryText: [host.providerType],
    value: host.externalId,
  };
};

export const autoCompleteOptionToGroupClassHost = (
  autoCompetion: AutoCompleteOption[],
): string[] => {
  return autoCompetion.map((completion: AutoCompleteOption) => {
    return completion.value;
  });
};

export const doesCurrentUserHaveRole = (
  currentUser: { providerTypeName?: string | null },
  role: CareTeamProviderType,
): boolean => {
  return currentUser?.providerTypeName === CareTeamProviderTypeNames[role];
};

export const parseUtcDate = (dateStr: string): DateTime | null => {
  const iso = DateTime.fromISO(dateStr, { zone: 'utc' });
  if (iso.isValid) return iso;

  const fallback = DateTime.fromFormat(dateStr, 'yyyy-MM-dd HH:mm:ss', {
    zone: 'utc',
  });
  if (fallback.isValid) return fallback;

  return null;
};

export const getProviderStateAtActionTime = (
  providerHistory: ProviderHistory[],
  actionAt: string,
): ProviderHistory | null => {
  if (!actionAt || !providerHistory?.length) return null;

  const actionDate = parseUtcDate(actionAt);
  if (!actionDate) return null;

  let latestBeforeAction: ProviderHistory | null = null;
  let latestTime = 0;

  const actionMillis = actionDate.toMillis();
  for (const record of providerHistory) {
    const createdAt = DateTime.fromISO(record.createdAt, { zone: 'utc' });
    if (!createdAt.isValid) continue;

    const createdMillis = createdAt.toMillis();

    if (createdMillis <= actionMillis && createdMillis > latestTime) {
      latestTime = createdMillis;
      latestBeforeAction = record;
    }
  }

  return latestBeforeAction;
};

export const getDisplayNameOrLegalNameForProvider = (
  displayFirstName: string | null,
  firstName: string | null,
  displayLastName: string | null,
  lastName: string | null,
): string => {
  const first = displayFirstName?.trim() || firstName || '';
  const last = displayLastName?.trim() || lastName || '';
  return `${first} ${last}`.trim();
};
